import { CHAT_MAX_LINKS, chatLinkToken } from '@shefira/shared';

/**
 * The items linked in the line being typed (ADR 0085). The input shows each
 * as its name in brackets; on sending, the names still there become the
 * tokens the server reads (`[#1]`), in the order they stand in the text. A
 * name the player has since erased or edited links nothing.
 */
export class ChatDraft {
  private pending: { itemId: string; label: string }[] = [];

  get size(): number {
    return this.pending.length;
  }

  /** Characters the labels take beyond their tokens: the input's room grows by as much. */
  get extraLength(): number {
    return this.pending.reduce(
      (sum, { label }, index) => sum + Math.max(0, label.length - chatLinkToken(index).length),
      0,
    );
  }

  /** Remembers `itemId` under `label`; false when the line already links as many as it may. */
  add(itemId: string, label: string): boolean {
    if (this.pending.length >= CHAT_MAX_LINKS) return false;
    this.pending.push({ itemId, label });
    return true;
  }

  /** The text to send and the ids its tokens stand for; the draft is emptied. */
  seal(text: string): { text: string; links: string[] } {
    const claimed: { start: number; end: number; itemId: string }[] = [];
    for (const { itemId, label } of this.pending) {
      let from = 0;
      for (;;) {
        const start = text.indexOf(label, from);
        if (start < 0) break;
        const end = start + label.length;
        if (claimed.every((range) => end <= range.start || start >= range.end)) {
          claimed.push({ start, end, itemId });
          break;
        }
        from = start + 1;
      }
    }
    this.pending = [];
    claimed.sort((a, b) => a.start - b.start);
    const kept = claimed.slice(0, CHAT_MAX_LINKS);
    let sealed = '';
    let cursor = 0;
    kept.forEach((range, index) => {
      sealed += text.slice(cursor, range.start) + chatLinkToken(index);
      cursor = range.end;
    });
    sealed += text.slice(cursor);
    return { text: sealed, links: kept.map((range) => range.itemId) };
  }

  clear(): void {
    this.pending = [];
  }
}
